import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Form, Spinner, Alert, Badge, Button, Row, Col } from 'react-bootstrap';
import { getTenantMembers, getMember, getMemberAssessments, uploadRoster } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const STATUS_VARIANT = { active: 'success', inactive: 'secondary', suspended: 'warning' };
const COMPLIANCE_VARIANT = { COMPLIANT: 'success', BREACH: 'danger', WARNING: 'warning' };

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

// Roster files are CSV (KAN-121 / KAN-165). These column ids are the member-record / identity fields the
// API takes at the top level; every other column is the member's advice profile and is nested under
// `profile`, so the sheet can carry the full KAN-121 field set without the API contract changing.
const ROSTER_ACCEPT = '.csv,text/csv';
const ROSTER_TOP_LEVEL = ['member_reference', 'email', 'registration_email', 'preferred_name', 'scheme_number'];

// Minimal RFC 4180 CSV parser → an array of string cells per row. Handles double-quoted fields and
// escaped quotes (""), plus commas / newlines inside quotes. Dependency-free — no spreadsheet library.
function parseCsvRows(text) {
    const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < src.length; i += 1) {
        const c = src[i];
        if (inQuotes) {
            if (c === '"' && src[i + 1] === '"') { field += '"'; i += 1; }
            else if (c === '"') { inQuotes = false; }
            else { field += c; }
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === ',') {
            row.push(field); field = '';
        } else if (c === '\n') {
            row.push(field); rows.push(row); row = []; field = '';
        } else {
            field += c;
        }
    }
    if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
    return rows;
}

// One CSV row → one roster member { member_reference, email, scheme_number?, preferred_name?, profile? }.
// Header cells are the myWaive field ids (see the onboarding template). `registration_email` is accepted
// as an alias for `email` (the template's id for the login address). Blank cells are dropped and every
// non-identity column is nested under `profile`. Values stay strings — canonical typing is the KAN-121
// field schema's job, not the upload form's.
function rowToMember(headers, cells) {
    const member = {};
    const profile = {};
    headers.forEach((rawKey, idx) => {
        const key = String(rawKey).trim();
        const val = (cells[idx] ?? '').trim();
        if (!key || val === '') return;
        if (key === 'registration_email') member.email = val;
        else if (ROSTER_TOP_LEVEL.includes(key)) member[key] = val;
        else profile[key] = val;
    });
    if (Object.keys(profile).length > 0) member.profile = profile;
    return member;
}

// Parse a CSV roster file into the member array the API expects. Throws SyntaxError (with a human
// reason) on an empty file or a row missing member_reference / email.
async function parseRosterFile(file) {
    const rows = parseCsvRows(await file.text());
    const headers = rows.length > 0 ? rows[0].map((h) => String(h).trim()) : [];
    const members = rows.slice(1)
        .map((cells) => rowToMember(headers, cells))
        .filter((m) => m.member_reference || m.email);
    if (members.length === 0) {
        throw new SyntaxError('no member rows found (expected a header row then one row per member)');
    }
    if (members.some((m) => !m.member_reference || !m.email)) {
        throw new SyntaxError('every row needs a member_reference and an email');
    }
    return members;
}

// Member plane (tech-debt §G) — read-only view of a tenant's members + each member's advice history.
// admin-api proxies to the spoke-side waive-member-api; the data stays in-spoke (ADR-0034/0038).
function Members({ tenantCode }) {
    const { can } = useAuth();
    const [members, setMembers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState(null);   // member detail: { ...member, identities }
    const [advice, setAdvice] = useState(null);        // { items, total }
    const [detailLoading, setDetailLoading] = useState(false);

    // Roster upload (KAN-155) — an onboarding action, separate from the member-reader-gated list below.
    const [rosterFile, setRosterFile] = useState(null);
    const [rosterBusy, setRosterBusy] = useState(false);
    const [rosterResult, setRosterResult] = useState(null);
    const [rosterError, setRosterError] = useState(null);

    const load = useCallback(async (term) => {
        if (!can.readMemberData()) { setLoading(false); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await getTenantMembers(tenantCode, term ? { search: term } : {});
            setMembers(res.items || []);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to load members.');
        } finally {
            setLoading(false);
        }
    }, [tenantCode, can]);

    useEffect(() => { load(''); }, [load]);

    const openMember = useCallback(async (m) => {
        setSelected(m);
        setAdvice(null);
        setDetailLoading(true);
        try {
            const [detail, adv] = await Promise.all([
                getMember(tenantCode, m.member_id),
                getMemberAssessments(tenantCode, m.member_id),
            ]);
            setSelected(detail);
            setAdvice(adv);
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to load member detail.');
        } finally {
            setDetailLoading(false);
        }
    }, [tenantCode]);

    const onSearch = (e) => { e.preventDefault(); load(search.trim()); };

    const handleRosterUpload = async (e) => {
        e.preventDefault();
        if (!rosterFile) return;
        setRosterBusy(true);
        setRosterError(null);
        setRosterResult(null);
        try {
            const members = await parseRosterFile(rosterFile);
            const res = await uploadRoster(tenantCode, members);
            setRosterResult(res);
            setRosterFile(null);
            if (can.readMemberData()) load('');   // refresh the list if we can see it
        } catch (err) {
            setRosterError(err instanceof SyntaxError
                ? `Invalid roster file — ${err.message}. Expected a CSV file with a header row and `
                  + 'columns member_reference and email (plus optional scheme_number, preferred_name and profile fields).'
                : err.response?.data?.detail || 'Roster upload failed.');
        } finally {
            setRosterBusy(false);
        }
    };

    const rosterCard = can.onboardTenant() && (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title style={{ fontSize: 16 }}>Roster upload</Card.Title>
                <Card.Text className="text-muted" style={{ fontSize: 13 }}>
                    Upload the employer roster — a <strong>CSV</strong> file, one row per member, with a
                    header row of myWaive field ids. <code>member_reference</code> and
                    {' '}<code>email</code> are required; other columns (e.g. <code>scheme_number</code>,
                    {' '}<code>preferred_name</code> and the advice-profile fields) are optional. Members are
                    provisioned into the tenant&apos;s member plane; for hosted tenants their logins are
                    created in the background.
                </Card.Text>
                {rosterError && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{rosterError}</Alert>}
                {rosterResult && (
                    <Alert variant="success" className="py-2" style={{ fontSize: 13 }}>
                        Accepted {rosterResult.members} member(s) — run <code>{rosterResult.run_id}</code>,
                        status {rosterResult.status}.
                    </Alert>
                )}
                <Form onSubmit={handleRosterUpload}>
                    <Row className="g-2 align-items-end">
                        <Col>
                            <Form.Group>
                                <Form.Label style={{ fontSize: 13 }}>Roster file (CSV)</Form.Label>
                                <Form.Control type="file" accept={ROSTER_ACCEPT} size="sm"
                                    data-testid="roster-file"
                                    onChange={(e) => setRosterFile(e.target.files?.[0] || null)} />
                            </Form.Group>
                        </Col>
                        <Col xs="auto">
                            <Button type="submit" variant="primary" size="sm" disabled={rosterBusy || !rosterFile}>
                                {rosterBusy ? <Spinner size="sm" animation="border" className="me-1" /> : null}
                                Upload roster
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
        </Card>
    );

    if (!can.readMemberData()) {
        return (
            <>
                {rosterCard}
                <Card>
                    <Card.Body>
                        <Card.Title>Members</Card.Title>
                        <Alert variant="secondary" className="py-2 mb-0" style={{ fontSize: 13 }}>
                            Member records and advice history are restricted. This view requires the
                            <strong> waive-member-reader</strong> role.
                        </Alert>
                    </Card.Body>
                </Card>
            </>
        );
    }

    return (
        <>
            {rosterCard}
            <Card>
            <Card.Body>
                <Card.Title>Members</Card.Title>
                <Card.Text className="text-muted">
                    The people this tenant has onboarded, and each member&apos;s advice history. Read-only —
                    read from the tenant&apos;s own spoke on demand (ADR-0034/0038).
                </Card.Text>

                <Form onSubmit={onSearch} className="mb-3">
                    <Row className="g-2">
                        <Col>
                            <Form.Control
                                placeholder="Search by member reference or email…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Search members"
                            />
                        </Col>
                        <Col xs="auto">
                            <Button type="submit" variant="outline-primary">Search</Button>
                        </Col>
                    </Row>
                </Form>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? <Spinner animation="border" size="sm" />
                    : members.length === 0 ? <p className="text-muted">No members found.</p>
                        : (
                            <Table hover responsive size="sm">
                                <thead>
                                    <tr><th>Member</th><th>Email</th><th>Status</th><th>Joined</th></tr>
                                </thead>
                                <tbody>
                                    {members.map((m) => (
                                        <tr key={m.member_id} style={{ cursor: 'pointer' }} onClick={() => openMember(m)}>
                                            <td>{m.external_member_id || m.member_id}</td>
                                            <td>{m.registration_email || '—'}</td>
                                            <td><Badge bg={STATUS_VARIANT[m.status] || 'secondary'}>{m.status}</Badge></td>
                                            <td>{fmtDate(m.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}

                {selected && (
                    <Card className="mt-3">
                        <Card.Body>
                            <Card.Title>
                                {selected.external_member_id || selected.member_id}{' '}
                                <Badge bg={STATUS_VARIANT[selected.status] || 'secondary'}>{selected.status}</Badge>
                            </Card.Title>
                            <div className="text-muted mb-2">{selected.registration_email || '—'}</div>

                            {detailLoading ? <Spinner animation="border" size="sm" /> : (
                                <>
                                    {selected.identities?.length > 0 && (
                                        <div className="mb-3">
                                            <strong>Logins:</strong>{' '}
                                            {selected.identities.map((i) => (
                                                <Badge key={`${i.issuer}|${i.idp_sub}`} bg="light" text="dark" className="me-1">
                                                    {i.issuer}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    <strong>Advice history</strong>
                                    {!advice || advice.items.length === 0
                                        ? <p className="text-muted mt-2">No advice runs for this member.</p>
                                        : (
                                            <Table responsive size="sm" className="mt-2">
                                                <thead>
                                                    <tr><th>Recommendation</th><th>Type</th><th>Compliance</th><th>Generated</th></tr>
                                                </thead>
                                                <tbody>
                                                    {advice.items.map((a) => (
                                                        <tr key={a.recommendation_id}>
                                                            <td><code>{a.recommendation_id}</code></td>
                                                            <td>{a.advice_type}</td>
                                                            <td>
                                                                <Badge bg={COMPLIANCE_VARIANT[a.overall_compliance_status] || 'secondary'}>
                                                                    {a.overall_compliance_status}
                                                                </Badge>
                                                            </td>
                                                            <td>{fmtDate(a.generated_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                </>
                            )}
                            <Button variant="link" className="p-0" onClick={() => { setSelected(null); setAdvice(null); }}>
                                Close
                            </Button>
                        </Card.Body>
                    </Card>
                )}
            </Card.Body>
            </Card>
        </>
    );
}

export default Members;
